import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react'

export type ToastKind = 'success' | 'error' | 'info' | 'warning'

interface ToastItem {
  id: number
  message: string
  kind: ToastKind
  duration: number
}

interface ShowToastOptions {
  duration?: number
}

interface ToastContextValue {
  showToast: (message: string, kind?: ToastKind, options?: ShowToastOptions) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

let toastCounter = 0

const DEFAULT_DURATION = 4000

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])

  const dismiss = useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const showToast = useCallback(
    (message: string, kind: ToastKind = 'info', options?: ShowToastOptions) => {
      const id = ++toastCounter
      const duration = options?.duration ?? DEFAULT_DURATION
      setToasts((prev) => [...prev, { id, message, kind, duration }])
    },
    [],
  )

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    throw new Error('useToast() debe usarse dentro de <ToastProvider>')
  }
  return ctx
}

interface ToastContainerProps {
  toasts: ToastItem[]
  onDismiss: (id: number) => void
}

function ToastContainer({ toasts, onDismiss }: ToastContainerProps) {
  if (toasts.length === 0) return null
  // top-20: debajo del header sticky (BUG-2 QA 4-jul: el toast tapaba los
  // enlaces del header y se leia a medias)
  return (
    <div className="fixed top-20 right-4 z-[60] flex flex-col gap-2 max-w-sm w-[calc(100%-2rem)] sm:w-auto pointer-events-none">
      {toasts.map((t) => (
        <ToastView key={t.id} toast={t} onDismiss={onDismiss} />
      ))}
    </div>
  )
}

interface ToastViewProps {
  toast: ToastItem
  onDismiss: (id: number) => void
}

// CRÍTICO Tanda 1 (5-jul): los toasts usaban bg-*-100/text-*-900 que el
// retema oscuro global (index.css) rompe — texto oscuro sobre fondo oscuro,
// y el info quedaba cyan sobre celeste = invisible. El toast es el canal de
// feedback de TODA la app. Ahora usa colores Hospital-Tech por estilo inline
// (inmune al override): panel navy translúcido + borde y texto del color del
// tipo, con contraste AA garantizado.
const KIND_INLINE: Record<ToastKind, CSSProperties> = {
  success: { background: '#15233a', borderColor: '#2FE08A', color: '#8af0c2' },
  error: { background: '#2a1622', borderColor: '#FF5C7A', color: '#ffb3c2' },
  warning: { background: '#2a2416', borderColor: '#F5A623', color: '#f7cf8f' },
  info: { background: '#152238', borderColor: '#7FD4FF', color: '#bfe6ff' },
}

const KIND_ICONS: Record<ToastKind, string> = {
  success: '✓',
  error: '✗',
  warning: '⚠',
  info: 'ℹ',
}

function ToastView({ toast, onDismiss }: ToastViewProps) {
  useEffect(() => {
    const handle = window.setTimeout(() => onDismiss(toast.id), toast.duration)
    return () => window.clearTimeout(handle)
  }, [toast.id, toast.duration, onDismiss])

  const s = KIND_INLINE[toast.kind]
  return (
    <div
      role="status"
      className="pointer-events-auto rounded-xl p-3 text-sm flex items-start gap-2"
      style={{
        background: s.background,
        border: `1px solid ${s.borderColor}`,
        borderLeft: `4px solid ${s.borderColor}`,
        color: s.color,
        boxShadow: '0 10px 30px -8px rgba(0,0,0,.6)',
      }}
    >
      <span className="font-bold leading-5" style={{ color: s.borderColor }}>
        {KIND_ICONS[toast.kind]}
      </span>
      <span className="flex-1 whitespace-pre-line">{toast.message}</span>
      <button
        onClick={() => onDismiss(toast.id)}
        className="opacity-60 hover:opacity-100 text-xs ml-2"
        style={{ color: s.color }}
        aria-label="Cerrar"
      >
        ✕
      </button>
    </div>
  )
}
